/**
 * Purchase Contract References — thin wrapper over the shared kho
 * `@/components/references`. Contract references không có header filter (page tự
 * khai — đều rỗng), gọi chung 1 URL /references/{pageKey}. Column-pref: localStorage.
 */
import { useMemo, useState } from 'react'

import {
  PURCHASE_CONTRACT_REFERENCE_ORDER,
  REFERENCE_CATALOG,
  type ReferencePageKey,
} from '@/components/references/catalog'
import ReferencesView from '@/components/references/ReferencesView'
import type { ReferenceMenuColumn } from '@/components/references/types'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { useLocalDatasheetColumns } from '@/utils/datasheetColumns/useLocalDatasheetColumns'
import { useGetPurchaseReferenceRowsQuery } from '../../store/api/purchaseContractApi'

export default function PurchaseReferencesView() {
  const [pageKey, setPageKey] = useState<ReferencePageKey>('value-updates')
  const [searchByPage, setSearchByPage] = useState<Partial<Record<ReferencePageKey, string>>>({})

  const page = REFERENCE_CATALOG[pageKey]
  const search = searchByPage[pageKey] ?? ''

  const queryArg = useMemo(() => ({ pageKey, limit: page.limit ?? 5000 }), [page.limit, pageKey])
  const { currentData, isFetching, error, refetch } = useGetPurchaseReferenceRowsQuery(queryArg, {
    refetchOnMountOrArgChange: true,
  })

  const menuColumns = useMemo<ReferenceMenuColumn[]>(
    () => page.columns.map((c) => ({ ...c, key: c.field, label: c.headerName })),
    [page.columns],
  )
  // Column layout keyed theo canonical page-key (dùng chung xuyên function):
  // cùng 1 page mở ở function nào cũng chung 1 layout width + ẩn/hiện, per-user.
  const columnsCtrl = useLocalDatasheetColumns({
    allColumns: menuColumns,
    storageKey: `references.${pageKey}`,
  })
  const widths = useColumnWidths(`references.${pageKey}`)

  const rows = currentData?.items ?? []
  return (
    <ReferencesView<ReferencePageKey>
      title="Purchase References"
      subtitle="Access switchboard parity for the 12 Purchase reference pages."
      pageOrder={PURCHASE_CONTRACT_REFERENCE_ORDER}
      configs={REFERENCE_CATALOG}
      pageKey={pageKey}
      onPageKeyChange={setPageKey}
      rows={rows}
      search={search}
      onSearchChange={(value) => setSearchByPage((prev) => ({ ...prev, [pageKey]: value }))}
      loading={isFetching && rows.length === 0}
      fetching={isFetching && rows.length > 0}
      error={error}
      onRetry={() => {
        void refetch()
      }}
      columnsCtrl={columnsCtrl}
      widths={widths}
      totalsMode="sum-flag"
    />
  )
}
